import { Loader2, Sparkles } from "lucide-react";
import { usePremium } from "../lib/premium";

interface Props {
  /** Set by the App when a `?checkout=success` redirect has just been
   *  consumed. Stays `true` while the post-checkout polling + listener
   *  race is running; the parent flips it back to `false` when the
   *  polling promise settles (success or timeout). */
  visible: boolean;
}

/**
 * Small floating affordance shown after a Stripe Checkout success
 * redirect while we're waiting for the `invertase/firestore-stripe-payments`
 * extension to finish its webhook and stamp the `stripeRole` claim.
 *
 * Auto-hides on TWO independent conditions, whichever comes first:
 *
 *   1. The parent's `visible` prop flips to `false` — happens when
 *      `pollPremiumUntilActive` resolves (claim landed, signed out,
 *      schedule ran out).
 *   2. `usePremium().isPremium` becomes `true` — happens the instant the
 *      `customers/{uid}/subscriptions` listener kicks
 *      `refreshPremium()` and the token sees the claim. This branch is
 *      important because the polling promise can still be in a `sleep`
 *      tick when the listener fires; the banner shouldn't sit visible
 *      for an extra second of "we already know you're premium".
 *
 * Sits just below the canvas tool row so it doesn't cover Snap or
 * Measure while the subscription claim is landing.
 */
export function PremiumActivatingBanner({ visible }: Props) {
  const premium = usePremium();
  if (!visible) return null;
  if (premium.isPremium) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="premium-activating-banner"
      className="absolute left-1/2 -translate-x-1/2 top-[calc(var(--topbar-h)+3.25rem)] z-20 flex items-center gap-2 rounded-full bg-white ring-1 ring-violet-200 px-3 py-1.5 shadow-sm"
    >
      <Sparkles className="size-3.5 text-violet-500 shrink-0" />
      <span className="text-caption text-ink-700">
        Activating your <span className="font-semibold">Premium</span>{" "}
        subscription
        <span className="text-ink-400">…</span>
      </span>
      <Loader2
        className="size-3.5 text-violet-500 shrink-0 animate-spin"
        aria-hidden="true"
      />
    </div>
  );
}
